import React, {useContext, useEffect, useMemo, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {AuthContext} from "../../config/AuthContext";
import NavBar from "../components/NavBar";
import Login from "../root/Login";
import {Button, Card, DatePicker, Select, Table, Tag} from "antd";
import {ColumnsType, TableProps} from "antd/es/table";
import Colors from "../../styles/Colors";
import {BiSearch} from "react-icons/bi";
import Historials from "../components/Historials";
import {GetEscolesLoop} from "../../dao/menudesign/GetEscolesLoop";
import {GetClassesEscola} from "../../dao/menudesign/GetClassesEscola";
import dayjs, {Dayjs} from "dayjs";
import toast, {Toaster} from "react-hot-toast";

const {RangePicker} = DatePicker;

interface ClasseRow {
    classe: string;
    nombre: number;
}

const Classes = () => {
    const titol = "Gestió de Classes";
    const {isLoggedIn, user, page} = useContext(AuthContext);

    const [escoles, setEscoles] = useState<string[]>([]);
    const [escola, setEscola] = useState<string | undefined>();
    const [rang, setRang] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()]);
    const [dades, setDades] = useState<ClasseRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [carregat, setCarregat] = useState(false);

    // Escoles permeses per a l'usuari (equivalent a usuari.getPermisosIdLoop())
    const esSuperusuari = user?.rol?.some((rol: any) => rol.nom === 'superusuari');
    const escolesPermeses: Set<string> = useMemo(() => {
        if (esSuperusuari) return new Set();
        return new Set(
            (user.escoles || [])
                .map((e: any) => (e.id_loop || '').toString().trim().toUpperCase())
                .filter((s: string) => s !== '')
        );
    }, [user, esSuperusuari]);

    useEffect(() => {
        if (isLoggedIn) carregarEscoles();
    }, [isLoggedIn]);

    async function carregarEscoles() {
        const resultat = await GetEscolesLoop(page);
        console.log('[Classes] escoles loop rebudes:', resultat);
        console.log('[Classes] esSuperusuari:', esSuperusuari, '| escolesPermeses:', Array.from(escolesPermeses));
        const noms = resultat
            .map((e: any) => e.escola as string)
            .filter((nom: string) => esSuperusuari || escolesPermeses.has((nom || '').trim().toUpperCase()));
        console.log('[Classes] escoles filtrades:', noms);
        setEscoles(noms);
    }

    async function cercar() {
        if (!escola) {
            toast.error("Selecciona una escola");
            return;
        }
        const [inici, fin] = rang;
        if (!inici || !fin) {
            toast.error("Selecciona un rang de dates");
            return;
        }
        if (inici.isAfter(fin)) {
            toast.error("La data d'inici ha de ser anterior a la final");
            return;
        }
        setLoading(true);
        const resultat = await GetClassesEscola(
            page,
            escola,
            inici.format('YYYY-MM-DD'),
            fin.format('YYYY-MM-DD')
        );
        setDades(resultat);
        setCarregat(true);
        setLoading(false);
    }

    const total = useMemo(
        () => dades.reduce((acc, d) => acc + (d.nombre || 0), 0),
        [dades]
    );

    const columns: ColumnsType<ClasseRow> = [
        {
            key: 'classe',
            title: 'Classe',
            dataIndex: 'classe',
            sorter: (a, b) => a.classe.localeCompare(b.classe),
        },
        {
            key: 'nombre',
            title: 'Cantitat',
            dataIndex: 'nombre',
            width: 160,
            sorter: (a, b) => a.nombre - b.nombre,
            render: (val) => <Tag color={Colors.colors.color4}>{val}</Tag>
        },
    ];

    const tableProps: TableProps<ClasseRow> = {loading};

    if (!isLoggedIn) return <Login/>;

    return (
        <div>
            <Toaster/>
            <NavBar/>
            <div className="general">
                <Card bordered className="elemento">
                    <h4 className="titol" style={{marginBottom: 16}}>{titol}</h4>

                    {/* Filtres: escola + rang de dates + botó cercar (equivalent al form del JSP) */}
                    <div
                         style={{
                             position: 'relative',
                             display: 'flex',
                             alignItems: 'center',
                             gap: 8,
                             flexWrap: 'wrap',
                             marginBottom: 16,
                             width: '100%',
                         }}>
                        <Select
                            placeholder="Selecciona escola..."
                            value={escola}
                            onChange={setEscola}
                            style={{width: 260}}
                            showSearch
                            optionFilterProp="label"
                            options={escoles.map(e => ({value: e, label: e}))}
                        />

                        <RangePicker
                            value={rang}
                            onChange={(v) => v && v[0] && v[1] && setRang([v[0], v[1]])}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            placeholder={['Del', 'fins al']}
                            style={{width: 280}}
                        />

                        <Button
                            title="Cercar"
                            type="primary"
                            style={{backgroundColor: Colors.colors.color2}}
                            loading={loading}
                            onClick={cercar}
                            icon={<BiSearch/>}
                        />
                    </div>

                    <Table
                        {...tableProps}
                        rowKey="classe"
                        columns={columns}
                        dataSource={dades}
                        pagination={false}
                        locale={{
                            emptyText: carregat
                                ? "Cap resultat a mostrar."
                                : "Selecciona una escola i un període vàlid i prem el botó de cercar."
                        }}
                        summary={() =>
                            dades.length > 0 ? (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>
                                        <b>TOTAL</b>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                        <b style={{color: 'red'}}>{total}</b>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            ) : null
                        }
                    />
                </Card>
                <br/>
                <Historials pantalla="CLASSES" pagina={page}/>
            </div>
        </div>
    );
};

export default Classes;
